import * as React from "react";
import {
  OnMemoryFileRefkeyExchanger,
  OnMemoryMyNumberRefkeyExchanger,
  FormSchema,
  Formula,
  FormSchemaConverter,
  FormData as GrafferFormData,
} from "@graffer-inc/form-schema";
import {
  FormWrapperPropsV2,
  grafferSchemasSuportTagNames,
  MarkdownProcessor,
} from "@graffer-inc/form-generator";
import {
  PaymentSettingV2,
  PaymentType,
  ProcedureDetailType,
} from "@graffer-inc/cloud-gov-core";
import { useFormGenViewModel } from "@graffer-inc/form-generator";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";
import { useDebouncedCallback } from "use-debounce";
import apiClient from "../../../../../../application/apis/api-client";
import {
  SmartApplyApiError,
  SmartApplyErrorCodeLiterals,
} from "@graffer-inc/smart-apply";
import { corporationSearchFormBaseUrl } from "../../../../../../utils/corporate-search/base-url";
import { addressSearchBaseUrl } from "../../../../../../utils/postal-code/base-url";
import { formMarkdownProcessorFactory } from "../../../../../../utils/markdown/processor";

export type CalcPaymentResult = {
  payment: PaymentType | null;
  errorCode: SmartApplyErrorCodeLiterals | undefined | null;
  status: CalcPaymentStatus;
};

type CalcPaymentStatus = "Success" | "Error" | "Calculating";

export const useInspectorModal = (
  procedureId: string,
  procedureDetail: ProcedureDetailType,
): {
  formProps: FormWrapperPropsV2;
  paymentResult: CalcPaymentResult;
  paymentSetting?: PaymentSettingV2;
} => {
  const [myNumberRefKeyExchanger] = React.useState(
    new OnMemoryMyNumberRefkeyExchanger(),
  );
  const [fileRefKeyExchanger] = React.useState(
    new OnMemoryFileRefkeyExchanger(),
  );
  const { formSchema } = procedureDetail.template;
  const { paymentSetting } = procedureDetail;
  const formSubmissionContext = React.useMemo(() => {
    return { submissionDate: new Date() };
  }, [formSchema]);
  const variables = React.useMemo(
    () =>
      paymentSetting?.items?.reduce<string[]>((formFieldIds, item) => {
        const formula = formSchema.formulaSet?.formulas.find(
          (f) => f.id === item.formulaId,
        );
        if (!formula) {
          return formFieldIds;
        }
        let vars: string[];
        try {
          vars = new Formula(formula).getVariables(formSchema);
        } catch (e) {
          reportUnexpectedError(e);
          return formFieldIds;
        }
        return [...formFieldIds, ...vars];
      }, []) ?? [],
    [formSchema, paymentSetting?.items],
  );

  const filteredVisibleFormSchema = React.useMemo(
    () =>
      FormSchema.fromJSON(
        new FormSchemaConverter(formSchema).filteredVisibleFormSchema(
          variables,
        ),
      ),
    [formSchema, variables],
  );
  const formProps = useFormGenViewModel({
    formSchema: filteredVisibleFormSchema,
    values: filteredVisibleFormSchema.toInitialDocument(),
    refKeyRegistry: () => ({
      ...myNumberRefKeyExchanger.store,
      ...fileRefKeyExchanger.store,
    }),
    formSubmissionContext,
  });

  const [paymentResult, setPaymentResult] = React.useState<CalcPaymentResult>({
    status: "Calculating",
    payment: null,
    errorCode: null,
  });
  const calcPayment = useDebouncedCallback(
    async (formData: GrafferFormData) => {
      if (!paymentSetting?.items) {
        return;
      }
      try {
        const { data } = await apiClient.calcPaymentProcedure(
          procedureId,
          formData,
          paymentSetting.items,
        );
        setPaymentResult({
          status: "Success",
          payment: data,
          errorCode: null,
        });
      } catch (e) {
        reportUnexpectedError(e);
        setPaymentResult({
          status: "Error",
          payment: null,
          errorCode: SmartApplyApiError.errorCode(e),
        });
      }
    },
    1000,
  );
  React.useEffect(() => {
    setPaymentResult({
      status: "Calculating",
      payment: null,
      errorCode: null,
    });
    calcPayment(formProps.values);
  }, [formProps.values]);

  const processor = formMarkdownProcessorFactory(grafferSchemasSuportTagNames);
  const smartApplyGrafferSchemasMarkdownProcessor: MarkdownProcessor = (
    content,
  ) => {
    return processor.processSync(content).result as React.ReactNode;
  };

  return {
    formProps: {
      ...formProps,
      refkeyContext: { fileRefKeyExchanger, myNumberRefKeyExchanger },
      submissionContext: formSubmissionContext,
      externalServiceUrlConfig: {
        corporationSearchFormBaseUrl: corporationSearchFormBaseUrl,
        addressSearchBaseUrl: addressSearchBaseUrl,
      },
      markdownConfig: {
        markdownProcessor: smartApplyGrafferSchemasMarkdownProcessor,
      },
    },
    paymentResult,
    paymentSetting,
  };
};
